import React, { useState, useEffect } from 'react';
import { Form, Button, Card, Alert, Badge, Row, Col, Spinner, Table, Modal, Dropdown } from 'react-bootstrap';
import api from '../../utils/api';

const TrelloManager = () => {
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  
  // Trello State
  const [apiKey, setApiKey] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [boardId, setBoardId] = useState('');
  const [listIdToDo, setListIdToDo] = useState('');
  const [listIdDoing, setListIdDoing] = useState('');
  const [listIdDone, setListIdDone] = useState('');
  const [isConfigured, setIsConfigured] = useState(false);
  
  // UI State
  const [boards, setBoards] = useState([]);
  const [lists, setLists] = useState([]);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingLists, setLoadingLists] = useState(false);
  const [loadingCards, setLoadingCards] = useState(false);
  const [activeList, setActiveList] = useState(null);

  // Card edit state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editCard, setEditCard] = useState(null);
  const [cardName, setCardName] = useState('');
  const [cardDesc, setCardDesc] = useState('');

  // Card move state
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveCardId, setMoveCardId] = useState(null);
  const [targetListId, setTargetListId] = useState('');

  useEffect(() => {
    fetchTrelloSettings();
  }, []);

  const fetchTrelloSettings = async () => {
    try {
      const { data } = await api.get('/trello/config');
      if (data && data._id) {
        setApiKey(data.apiKey || '');
        setApiToken(data.apiToken || '');
        setBoardId(data.boardId || '');
        setListIdToDo(data.listIdToDo || '');
        setListIdDoing(data.listIdDoing || '');
        setListIdDone(data.listIdDone || '');
        setIsConfigured(true);
        
        // If we have credentials and a board, fetch the lists
        if (data.apiKey && data.apiToken) {
          fetchBoards();
          if (data.boardId) {
            fetchLists(data.boardId);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching Trello settings:', err);
    }
  };

  const fetchBoards = async () => {
    if (!apiKey || !apiToken) return;
    
    setLoading(true);
    try {
      const { data } = await api.get('/trello/boards');
      setBoards(data);
    } catch (err) {
      console.error('Trello board fetch error:', err);
      
      // Get detailed error message
      let errorMsg = 'Failed to fetch Trello boards. Check your API credentials.';
      
      if (err.response && err.response.data) {
        const respData = err.response.data;
        // If we have detailed error info from our backend
        if (respData.details) {
          errorMsg += ` Details: ${respData.details}`;
        } else if (respData.error) {
          errorMsg += ` Error: ${JSON.stringify(respData.error)}`;
        } else if (typeof respData === 'string') {
          errorMsg += ` Server message: ${respData}`;
        }
      }
      
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const fetchLists = async (selectedBoardId) => {
    if (!selectedBoardId) return;
    
    setLoadingLists(true);
    try {
      const { data } = await api.get(`/trello/lists/${selectedBoardId}`);
      setLists(data);
    } catch (err) {
      console.error('Trello lists fetch error:', err);
      
      let errorMsg = 'Failed to fetch lists for the selected board.';
      
      if (err.response && err.response.data) {
        const respData = err.response.data;
        if (respData.details) {
          errorMsg += ` Details: ${respData.details}`;
        } else if (respData.error) {
          errorMsg += ` Error: ${JSON.stringify(respData.error)}`;
        }
      }
      
      setError(errorMsg);
    } finally {
      setLoadingLists(false);
    }
  };

  const handleBoardChange = (e) => {
    const selectedBoardId = e.target.value;
    setBoardId(selectedBoardId);
    
    // Reset list selections when board changes
    setListIdToDo('');
    setListIdDoing('');
    setListIdDone('');
    
    if (selectedBoardId) {
      fetchLists(selectedBoardId);
    } else {
      setLists([]);
    }
  };

  const testTrelloCredentials = async () => {
    if (!apiKey || !apiToken) {
      return false;
    }
    
    try {
      // Using the regular /boards endpoint to test credentials
      const { data } = await api.get('/trello/boards');
      return data && Array.isArray(data);
    } catch (err) {
      console.error('Trello credential test failed:', err);
      
      let errorMsg = 'Invalid Trello API credentials.';
      
      if (err.response && err.response.data) {
        const respData = err.response.data;
        if (respData.details) {
          errorMsg += ` Details: ${respData.details}`;
        } else if (respData.error) {
          errorMsg += ` Error: ${JSON.stringify(respData.error)}`;
        }
      }
      
      setError(errorMsg);
      return false;
    }
  };
  
  const handleCredentialsSave = async (e) => {
    e.preventDefault();
    setMessage(null);
    setError(null);
    
    try {
      // First test the credentials
      const isValid = await testTrelloCredentials();
      
      if (!isValid) {
        setError('Failed to connect to Trello API with provided credentials. Please check your API key and token.');
        return;
      }
      
      // If valid, save the credentials
      await api.put('/trello/config', {
        apiKey,
        apiToken,
        boardId,
        listIdToDo,
        listIdDoing,
        listIdDone
      });
      
      setMessage('Trello settings saved successfully!');
      setIsConfigured(true);
      
      // Fetch boards again with credentials we know are good
      fetchBoards();
    } catch (err) {
      console.error('Error saving Trello settings:', err);
      setError('Failed to save Trello settings.');
    }
  };

  const handleBoardConfigSave = async (e) => {
    e.preventDefault();
    setMessage(null);
    setError(null);
    
    try {
      await api.put('/trello/config', {
        apiKey,
        apiToken,
        boardId,
        listIdToDo,
        listIdDoing,
        listIdDone
      });
      
      setMessage('Trello board configuration saved successfully!');
    } catch (err) {
      setError('Failed to save board configuration.');
    }
  };
  
  // Fetch cards from a specific list
  const fetchCards = async (listId) => {
    if (!listId) return;
    
    setLoadingCards(true);
    setActiveList(listId);
    
    try {
      const { data } = await api.get(`/trello/cards?listId=${listId}`);
      setCards(data);
    } catch (err) {
      setError('Failed to fetch cards from this list.');
    } finally {
      setLoadingCards(false);
    }
  };
  
  // Move a card to a different list
  const handleMoveCard = async () => {
    if (!moveCardId || !targetListId) return;
    
    try {
      await api.put(`/trello/cards/${moveCardId}/move`, { listId: targetListId });
      setMessage('Card moved successfully!');
      
      // Refresh the cards in the current list
      fetchCards(activeList);
      
      // Close the modal
      setShowMoveModal(false);
      setMoveCardId(null);
      setTargetListId('');
    } catch (err) {
      setError('Failed to move card.');
    }
  };
  
  // Open the edit modal for a card
  const openEditModal = (card) => {
    setEditCard(card);
    setCardName(card.name);
    setCardDesc(card.desc || '');
    setShowEditModal(true);
  };
  
  // Update a card's details
  const handleUpdateCard = async () => {
    if (!editCard) return;
    
    try {
      await api.put(`/trello/cards/${editCard.id}`, {
        name: cardName,
        desc: cardDesc
      });
      setMessage('Card updated successfully!');
      
      // Refresh the cards
      fetchCards(activeList);
      
      // Close the modal
      setShowEditModal(false);
      setEditCard(null);
    } catch (err) {
      setError('Failed to update card.');
    }
  };
  
  // Archive a card
  const handleArchiveCard = async (cardId) => {
    if (!cardId) return;
    
    if (!window.confirm('Are you sure you want to archive this card?')) {
      return;
    }
    
    try {
      await api.put(`/trello/cards/${cardId}/archive`);
      setMessage('Card archived successfully!');
      
      // Refresh the cards
      fetchCards(activeList);
    } catch (err) {
      setError('Failed to archive card.');
    }
  };

  return (
    <div className="d-flex justify-content-center flex-column">
      <Card style={{ width: '100%', maxWidth: '800px', marginBottom: '20px' }} className="align-self-center">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h3 className="mb-0">Trello Integration</h3>
            {isConfigured ? 
              <Badge bg="success">Configured</Badge> : 
              <Badge bg="secondary">Not Configured</Badge>
            }
          </div>
          
          {message && <Alert variant="success" onClose={() => setMessage(null)} dismissible>{message}</Alert>}
          {error && (
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              <Alert.Heading>Error</Alert.Heading>
              <p style={{ whiteSpace: 'pre-line' }}>{error}</p>
            </Alert>
          )}
          
          <hr />
          
          {/* API Credentials Section */}
          <h5 className="mb-3">API Credentials</h5>
          <Form onSubmit={handleCredentialsSave}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>API Key</Form.Label>
                  <Form.Control
                    type="text"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    required
                  />
                  <Form.Text className="text-muted">
                    Get from <a href="https://trello.com/app-key" target="_blank" rel="noopener noreferrer">Trello API Key page</a>
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>API Token</Form.Label>
                  <Form.Control
                    type="password"
                    value={apiToken}
                    onChange={(e) => setApiToken(e.target.value)}
                    required
                    placeholder={apiToken ? "(Unchanged)" : ""}
                  />
                </Form.Group>
              </Col>
            </Row>
            <div className="d-flex gap-2">
              <Button 
                variant="primary" 
                type="submit" 
                className="mb-4"
                disabled={!apiKey || !apiToken}
              >
                Save Credentials
              </Button>
              
              <Button 
                variant="outline-primary" 
                className="mb-4"
                onClick={(e) => {
                  e.preventDefault();
                  setMessage(null);
                  setError(null);
                  testTrelloCredentials().then(isValid => {
                    if (isValid) {
                      setMessage("Trello API credentials are valid!");
                    }
                  });
                }}
                disabled={!apiKey || !apiToken}
              >
                Test Connection
              </Button>
            </div>
          </Form>
          
          {/* Board Configuration Section */}
          {(apiKey && apiToken) && (
            <>
              <h5 className="mb-3 mt-4">Board Configuration</h5>
              <Form onSubmit={handleBoardConfigSave}>
                <Form.Group className="mb-3">
                  <Form.Label>Select Board</Form.Label>
                  <div className="d-flex gap-2 align-items-center">
                    <Form.Select 
                      value={boardId} 
                      onChange={handleBoardChange}
                      disabled={loading || boards.length === 0}
                    >
                      <option value="">-- Select a board --</option>
                      {boards.map(board => (
                        <option key={board.id} value={board.id}>{board.name}</option>
                      ))}
                    </Form.Select>
                    {loading && <Spinner animation="border" size="sm" />}
                    <Button 
                      variant="outline-secondary" 
                      size="sm"
                      onClick={fetchBoards}
                      disabled={loading}
                    >
                      Refresh
                    </Button>
                  </div>
                </Form.Group>
                
                {boardId && (
                  <div className="mt-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">List Mappings</h6>
                      {loadingLists && <Spinner animation="border" size="sm" />}
                    </div>
                    
                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>To Do List</Form.Label>
                          <Form.Select 
                            value={listIdToDo} 
                            onChange={(e) => setListIdToDo(e.target.value)}
                            disabled={loadingLists || lists.length === 0}
                          >
                            <option value="">-- Select list --</option>
                            {lists.map(list => (
                              <option key={list.id} value={list.id}>{list.name}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Doing List</Form.Label>
                          <Form.Select 
                            value={listIdDoing} 
                            onChange={(e) => setListIdDoing(e.target.value)}
                            disabled={loadingLists || lists.length === 0}
                          >
                            <option value="">-- Select list --</option>
                            {lists.map(list => (
                              <option key={list.id} value={list.id}>{list.name}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Done List</Form.Label>
                          <Form.Select 
                            value={listIdDone} 
                            onChange={(e) => setListIdDone(e.target.value)}
                            disabled={loadingLists || lists.length === 0}
                          >
                            <option value="">-- Select list --</option>
                            {lists.map(list => (
                              <option key={list.id} value={list.id}>{list.name}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                    
                    <Button 
                      variant="primary" 
                      type="submit" 
                      disabled={!boardId}
                    >
                      Save Board Configuration
                    </Button>
                  </div>
                )}
              </Form>
            </>
          )}
          
          {/* Help Section */}
          <div className="mt-5">
            <h5>Setup Instructions</h5>
            <ol className="ps-4 mt-2">
              <li>Get your API key from <a href="https://trello.com/app-key" target="_blank" rel="noopener noreferrer">Trello API Key page</a></li>
              <li>Generate a token using the link on that page</li>
              <li>Enter your API key and token in the form above</li>
              <li>Select the board you want to use</li>
              <li>Map the To Do, Doing, and Done lists from your board</li>
            </ol>
          </div>
        </Card.Body>
      </Card>
      
      {/* Card Management Section */}
      {isConfigured && (
        <Card style={{ width: '100%', maxWidth: '800px' }} className="align-self-center mb-4">
          <Card.Body>
            <h4>Card Management</h4>
            <p className="text-muted">View and manage your Trello cards</p>
            
            <div className="d-flex gap-2 mb-4">
              <Button 
                variant="outline-primary" 
                onClick={() => fetchCards(listIdToDo)}
                disabled={!listIdToDo}
              >
                View To Do
              </Button>
              <Button 
                variant="outline-primary" 
                onClick={() => fetchCards(listIdDoing)}
                disabled={!listIdDoing}
              >
                View Doing
              </Button>
              <Button 
                variant="outline-primary" 
                onClick={() => fetchCards(listIdDone)}
                disabled={!listIdDone}
              >
                View Done
              </Button>
            </div>
            
            {loadingCards ? (
              <div className="text-center py-4">
                <Spinner animation="border" />
              </div>
            ) : cards.length > 0 ? (
              <Table striped hover responsive>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Description</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {cards.map(card => (
                    <tr key={card.id}>
                      <td>{card.name}</td>
                      <td>{card.desc ? (card.desc.length > 50 ? `${card.desc.substring(0, 50)}...` : card.desc) : 'No description'}</td>
                      <td>
                        <Dropdown>
                          <Dropdown.Toggle variant="outline-secondary" size="sm" id={`dropdown-${card.id}`}>
                            Actions
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item onClick={() => openEditModal(card)}>
                              Edit Details
                            </Dropdown.Item>
                            <Dropdown.Item 
                              onClick={() => {
                                setMoveCardId(card.id);
                                setShowMoveModal(true);
                              }}
                            >
                              Move Card
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item 
                              onClick={() => handleArchiveCard(card.id)}
                              className="text-danger"
                            >
                              Archive Card
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : activeList ? (
              <Alert variant="info">No cards found in this list.</Alert>
            ) : (
              <Alert variant="info">Select a list to view cards.</Alert>
            )}
          </Card.Body>
        </Card>
      )}
      
      {/* Edit Card Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Card</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Card Name</Form.Label>
              <Form.Control 
                type="text" 
                value={cardName} 
                onChange={(e) => setCardName(e.target.value)} 
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control 
                as="textarea" 
                rows={3} 
                value={cardDesc} 
                onChange={(e) => setCardDesc(e.target.value)} 
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpdateCard} disabled={!cardName.trim()}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Move Card Modal */}
      <Modal show={showMoveModal} onHide={() => setShowMoveModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Move Card</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Select Destination List</Form.Label>
            <Form.Select 
              value={targetListId} 
              onChange={(e) => setTargetListId(e.target.value)}
              required
            >
              <option value="">-- Select a list --</option>
              {lists.map(list => (
                <option key={list.id} value={list.id}>{list.name}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowMoveModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleMoveCard} disabled={!targetListId}>
            Move Card
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TrelloManager;